/**
 * MA TRAN 2D CUA PDF - va MOT quy uoc nhan duy nhat.
 *
 * PDF dung VECTO HANG: mot diem p duoc bien doi thanh p x M. He qua truc tiep:
 * "ap M truoc roi ap N" viet la M x N, KHONG phai N x M. Dao thu tu la loi khong
 * bao gio nhin thay tren mot ma tran ti le, va luon nhin thay tren mot ma tran
 * co xoay - tuc la no chi lo ra tren dung nhung tai lieu kho nhat.
 *
 * Toan tu `cm` trong content stream cung theo quy uoc do: `a b c d e f cm` NHAN
 * TRAI vao CTM dang co (CTM' = cm x CTM).
 */
import type { PdfMatrix, PdfRect } from '@pdfeditor/types';

export const IDENTITY: PdfMatrix = [1, 0, 0, 1, 0, 0];

/** A roi B. Tuong duong p x A x B. */
export function mul(a: PdfMatrix, b: PdfMatrix): PdfMatrix {
  return [
    a[0] * b[0] + a[1] * b[2],
    a[0] * b[1] + a[1] * b[3],
    a[2] * b[0] + a[3] * b[2],
    a[2] * b[1] + a[3] * b[3],
    a[4] * b[0] + a[5] * b[2] + b[4],
    a[4] * b[1] + a[5] * b[3] + b[5],
  ];
}

export function applyPoint(m: PdfMatrix, x: number, y: number): { x: number; y: number } {
  return { x: m[0] * x + m[2] * y + m[4], y: m[1] * x + m[3] * y + m[5] };
}

/**
 * Ma tran nghich dao, hoac null khi ma tran SUY BIEN.
 *
 * Suy bien la truong hop that, khong phai gia dinh phong thu: mot anh bi thu ve
 * be rong 0 van la mot lenh ve hop le trong tep. Tra ve null de ben goi TU CHOI
 * thao tac thay vi sinh ra mot ma tran toan Infinity roi ghi no vao trang.
 */
export function inverse(m: PdfMatrix): PdfMatrix | null {
  const det = m[0] * m[3] - m[1] * m[2];
  if (!Number.isFinite(det) || Math.abs(det) < 1e-12) return null;
  const a = m[3] / det;
  const b = -m[1] / det;
  const c = -m[2] / det;
  const d = m[0] / det;
  return [a, b, c, d, -(m[4] * a + m[5] * c), -(m[4] * b + m[5] * d)];
}

/**
 * HOP BAO cua o vuong don vi sau khi qua ma tran.
 *
 * Anh trong PDF luon duoc ve trong o vuong (0,0)-(1,1); toan bo vi tri va kich
 * thuoc nam trong ma tran. Nen "hinh chu nhat cua anh tren trang" chinh la hop
 * bao cua bon goc o vuong do.
 */
export function unitSquareBounds(m: PdfMatrix): PdfRect {
  const corners = [applyPoint(m, 0, 0), applyPoint(m, 1, 0), applyPoint(m, 0, 1), applyPoint(m, 1, 1)];
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

/**
 * Phep bien doi (trong he toa do TRANG) dua hop bao `from` thanh hop bao `to`.
 *
 * Tra ve null khi `from` bet theo mot chieu: khong co he so co gian nao dua mot
 * doan thang thanh mot hinh chu nhat, va doan mot he so vo can chi lam anh nhay
 * di mot cho ngau nhien.
 *
 * VI SAO KHONG DUNG THANG [w 0 0 h x y]: cong thuc do vut bo moi phep XOAY va
 * LAT co san trong ma tran cu. Mot anh dat nghieng 15 do se bat ngo dung thang
 * len ngay khi nguoi dung keo no sang phai mot chut - mot thay doi ho khong he
 * yeu cau. Nhan them mot phep bien doi vao ma tran cu thi phan xoay duoc giu.
 */
export function rectToRectTransform(from: PdfRect, to: PdfRect): PdfMatrix | null {
  if (Math.abs(from.width) < 1e-9 || Math.abs(from.height) < 1e-9) return null;
  const sx = to.width / from.width;
  const sy = to.height / from.height;
  return [sx, 0, 0, sy, to.x - from.x * sx, to.y - from.y * sy];
}

/**
 * MA TRAN `cm` MOI cho mot anh vua bi keo.
 *
 * @param localCm  ma tran `cm` dang nam trong content stream (phan CUC BO)
 * @param fullCtm  ma tran DAY DU pdf.js bao cao (cuc bo da nhan voi moi phep
 *                 bien doi bao ngoai)
 * @param toRect   hop chu nhat dich, he toa do TRANG
 *
 * Phep tinh: goi outer la phan bao ngoai, full = local x outer. Ta muon
 * full' = full x T voi T la phep bien doi do duoc trong he TRANG. Vay
 *      local' = full' x outer^-1 = full x T x full^-1 x local.
 * Ca ba thua so deu biet, nen khong can doan phan bao ngoai la gi - va do la
 * diem mau chot: phan bao ngoai co the chua ca phep xoay trang lan phep bien
 * doi cua mot Form XObject long nhau.
 */
export function retargetLocalMatrix(
  localCm: PdfMatrix,
  fullCtm: PdfMatrix,
  toRect: PdfRect,
): PdfMatrix | null {
  const fromRect = unitSquareBounds(fullCtm);
  const t = rectToRectTransform(fromRect, toRect);
  if (!t) return null;
  const invFull = inverse(fullCtm);
  if (!invFull) return null;
  return mul(mul(mul(fullCtm, t), invFull), localCm);
}

/**
 * So thuc viet ra content stream.
 *
 * KHONG dung ky hieu mu (1e-7): content stream cua PDF khong chap nhan no, va
 * mot bo doc nghiem tuc se coi ca luong la hong. Toan tu quen mat nay tao ra
 * dung mot trang trang - kieu hong dat nhat co the.
 */
export function formatNumber(v: number): string {
  if (!Number.isFinite(v)) return '0';
  const rounded = Math.round(v * 1e6) / 1e6;
  if (Object.is(rounded, -0)) return '0';
  const s = rounded.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
  return s === '' || s === '-' ? '0' : s;
}

export function formatMatrix(m: PdfMatrix): string {
  return m.map(formatNumber).join(' ');
}
